/**
 * MorningBriefHero — "Today's 3 Things That Matter" hero for the client portal.
 *
 * Layout (from Figma/user reference):
 *   ┌────────────────────────────────────────────────────────────┐
 *   │              Today's 3 Things That Matter                  │
 *   │  Focus here — these are the moves that push the business…  │
 *   │                                                            │
 *   │  [ Priority (green) ] [ Risk (red) ] [ Opportunity (indigo)]│
 *   │                                                            │
 *   │  [ Cash Flow Overview ] [ Health Score ] [ Daybook Coach ] │
 *   │                                                            │
 *   │  [ Data safe ]  [ Real accountants ]  [ 24/7 monitoring ]  │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Silent if no published brief exists yet — the hero is opt-in per-client.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, CheckCircle2, Plus, Lock, UserRound, Clock, ArrowRight, Info, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const NZD_2 = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

function fmtValue(v, kind) {
    if (v == null || Number.isNaN(v)) return "—";
    if (kind === "money") return Math.abs(v) < 1000 ? NZD_2.format(v) : NZD.format(v);
    if (kind === "percent") return `${v > 0 ? "+" : ""}${v}%`;
    if (kind === "ratio") return v.toFixed(2);
    if (kind === "decimal") return v.toFixed(1);
    if (kind === "count") return String(v);
    return String(v);
}

export default function MorningBriefHero() {
    const { activeClientId } = useAuth();
    const [brief, setBrief] = useState(null);
    const [taxContext, setTaxContext] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [sourcesOpen, setSourcesOpen] = useState(false);

    useEffect(() => {
        setLoaded(false);
        const params = activeClientId ? { client_id: activeClientId } : {};
        api.get("/morning-brief/latest", { params })
            .then((r) => {
                setBrief(r.data?.brief || null);
                setTaxContext(r.data?.tax_context || null);
            })
            .catch(() => { setBrief(null); setTaxContext(null); })
            .finally(() => setLoaded(true));
    }, [activeClientId]);

    // Render nothing until we know the answer, and nothing if no brief yet.
    if (!loaded || !brief) return null;

    return (
        <section className="rounded-2xl bg-[#F7F7F5] px-4 py-8 sm:px-8 sm:py-10" data-testid="morning-brief-hero">
            <div className="text-center max-w-2xl mx-auto">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#111]">
                    Today&apos;s 3 Things That Matter
                </h2>
                <p className="mt-2 text-sm text-[#5E636E]">
                    Focus here — these are the moves that push the business forward.
                </p>
            </div>

            {/* Row 1 — 3 insight cards */}
            <div className="grid gap-4 sm:grid-cols-3 mt-6">
                <InsightCard
                    kind="priority"
                    insight={brief.priority}
                    eyebrow="Today's Priority"
                    Icon={Plus}
                    accent="emerald"
                    testId="brief-priority"
                    onShowSources={() => setSourcesOpen(true)}
                />
                <InsightCard
                    kind="risk"
                    insight={brief.risk}
                    eyebrow="Biggest Risk"
                    Icon={AlertOctagon}
                    accent="rose"
                    testId="brief-risk"
                    onShowSources={() => setSourcesOpen(true)}
                />
                <InsightCard
                    kind="opportunity"
                    insight={brief.opportunity}
                    eyebrow="Biggest Opportunity"
                    Icon={CheckCircle2}
                    accent="indigo"
                    testId="brief-opportunity"
                    onShowSources={() => setSourcesOpen(true)}
                />
            </div>

            {sourcesOpen && (
                <SourcesModal clientId={activeClientId} onClose={() => setSourcesOpen(false)} />
            )}

            {/* Row 2 — cash / health / coach */}
            <div className="grid gap-4 lg:grid-cols-3 mt-4">
                <CashFlowCard brief={brief} taxContext={taxContext} />
                <HealthScoreCard brief={brief} />
                <DaybookCoachCard brief={brief} />
            </div>

            {/* Trust bar */}
            <div className="grid gap-4 sm:grid-cols-3 mt-8 pt-6 border-t border-[#E4E4E7]">
                <TrustItem Icon={Lock} title="Your data is safe and secure" body="Bank-level security, never shared." />
                <TrustItem Icon={UserRound} title="Backed by real accountants" body="Our team is here when you need them." />
                <TrustItem Icon={Clock} title="Here 24/7" body="Daybook checks your numbers around the clock." />
            </div>
        </section>
    );
}

// -------------------------------------------------- InsightCard

const ACCENTS = {
    emerald: {
        dot: "bg-emerald-500 text-white",
        label: "text-emerald-700",
        btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
    },
    rose: {
        dot: "bg-rose-500 text-white",
        label: "text-rose-700",
        btn: "bg-rose-500 hover:bg-rose-600 text-white",
    },
    indigo: {
        dot: "bg-indigo-500 text-white",
        label: "text-indigo-700",
        btn: "bg-indigo-500 hover:bg-indigo-600 text-white",
    },
};

function InsightCard({ insight, eyebrow, Icon, accent, testId, onShowSources }) {
    if (!insight?.title) return <div className="hidden" data-testid={`${testId}-empty`} />;
    const a = ACCENTS[accent];
    const cta = insight.cta_link ? (
        insight.cta_link.startsWith("http") ? (
            <a
                href={insight.cta_link}
                target="_blank"
                rel="noreferrer"
                className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium ${a.btn}`}
                data-testid={`${testId}-cta`}
            >
                {insight.cta_label || "Take action"} <ArrowRight className="h-4 w-4" />
            </a>
        ) : (
            <Link
                to={insight.cta_link}
                className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium ${a.btn}`}
                data-testid={`${testId}-cta`}
            >
                {insight.cta_label || "Take action"} <ArrowRight className="h-4 w-4" />
            </Link>
        )
    ) : null;

    return (
        <div className="rounded-2xl bg-white border border-[#E4E4E7] p-5 flex flex-col" data-testid={testId}>
            <div className="flex items-center gap-2">
                <span className={`grid h-6 w-6 place-items-center rounded-full ${a.dot}`}>
                    <Icon className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${a.label}`}>{eyebrow}</span>
            </div>
            <h3 className="mt-3 font-display text-lg leading-snug text-[#111]" data-testid={`${testId}-title`}>
                {insight.title}
            </h3>
            {insight.why_it_matters && (
                <>
                    <div className="mt-4 text-[13px] font-semibold text-[#111]">Why this matters</div>
                    <p className="mt-1 text-sm text-[#5E636E] leading-relaxed" data-testid={`${testId}-why`}>
                        {insight.why_it_matters}
                    </p>
                    {onShowSources && (
                        <button
                            type="button"
                            onClick={onShowSources}
                            className={`mt-2 inline-flex items-center gap-1 text-[11px] font-medium ${a.label} hover:underline underline-offset-2 decoration-dotted`}
                            data-testid={`${testId}-sources-btn`}
                        >
                            <Info className="h-3 w-3" /> Where do these numbers come from?
                        </button>
                    )}
                </>
            )}
            <div className="mt-auto">{cta}</div>
        </div>
    );
}

// -------------------------------------------------- CashFlowCard

function CashFlowCard({ brief, taxContext }) {
    const series = Array.isArray(brief.cash_series) ? brief.cash_series : [];
    const max = series.reduce((m, p) => Math.max(m, p.amount || 0), 0) || 1;
    const balance = brief.cash_balance;

    return (
        <div className="rounded-2xl bg-white border border-[#E4E4E7] p-5" data-testid="brief-cashflow">
            <div className="flex items-baseline justify-between">
                <div className="text-sm font-semibold text-[#111]">Cash Flow Overview</div>
                <div className="text-xs text-[#5E636E]">{brief.cash_period_label || "Last 60 days"}</div>
            </div>
            <div className="mt-3 font-display text-3xl font-semibold text-[#111]" data-testid="brief-cash-balance">
                {balance == null ? "—" : NZD.format(balance)}
            </div>
            <div className="text-xs text-[#5E636E]">Current cash balance</div>
            {taxContext && taxContext.safe_to_spend != null && (
                <div className="mt-2 flex items-center justify-between rounded-lg bg-[#F5F4EF] px-3 py-2" data-testid="brief-safe-to-spend">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Safe to spend</div>
                        <div className="text-lg font-semibold text-[#0E3B2E]">{NZD.format(taxContext.safe_to_spend)}</div>
                    </div>
                    <div className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        taxContext.funding_status === "Fully Funded" ? "bg-emerald-100 text-emerald-800"
                        : taxContext.funding_status === "Partially Funded" ? "bg-amber-100 text-amber-800"
                        : taxContext.funding_status === "Underfunded" ? "bg-rose-100 text-rose-800"
                        : "bg-[#E4E4E7] text-[#5E636E]"
                    }`}>{taxContext.funding_status}</div>
                </div>
            )}
            <div className="mt-5 flex items-end gap-1.5 h-24">
                {series.length === 0 ? (
                    <div className="text-xs text-[#5E636E]">No trend data yet.</div>
                ) : (
                    series.map((pt, i) => {
                        const h = Math.max(4, (pt.amount / max) * 100);
                        const isLast = i === series.length - 1;
                        return (
                            <div
                                key={`${pt.label}-${i}`}
                                className={`flex-1 rounded-sm ${isLast ? "bg-indigo-500" : "bg-[#D8DBE2]"}`}
                                style={{ height: `${h}%` }}
                                title={`${pt.label}: ${NZD.format(pt.amount)}`}
                            />
                        );
                    })
                )}
            </div>
            {series.length > 0 && (
                <div className="mt-1.5 flex justify-between text-[10px] text-[#5E636E]">
                    <span>{series[0].label}</span>
                    <span>{series[Math.floor(series.length / 2)]?.label}</span>
                    <span>{series[series.length - 1].label}</span>
                </div>
            )}
        </div>
    );
}

// -------------------------------------------------- HealthScoreCard

const HEALTH_ROWS = [
    { key: "cash_flow", label: "Cash Flow" },
    { key: "profitability", label: "Profitability" },
    { key: "tax_readiness", label: "Tax Readiness" },
    { key: "growth", label: "Growth" },
    { key: "efficiency", label: "Efficiency" },
];

function HealthScoreCard({ brief }) {
    const score = brief.health_score;
    const b = brief.health_breakdown || {};
    const rating = score == null ? "—" : score >= 80 ? "Good" : score >= 60 ? "OK" : "Attention";
    const stroke = score == null ? 0 : Math.max(0, Math.min(100, score));
    const dash = `${(stroke / 100) * 251.2} 251.2`;

    return (
        <div className="rounded-2xl bg-white border border-[#E4E4E7] p-5" data-testid="brief-health">
            <div className="text-sm font-semibold text-[#111]">Business Health Score</div>
            <div className="mt-3 flex items-center justify-center">
                <div className="relative h-32 w-32">
                    <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                        <circle cx="50" cy="50" r="40" strokeWidth="8" stroke="#E4E4E7" fill="none" />
                        <circle
                            cx="50" cy="50" r="40" strokeWidth="8" stroke="#10b981" fill="none"
                            strokeDasharray={dash} strokeLinecap="round"
                        />
                    </svg>
                    <div className="absolute inset-0 grid place-items-center">
                        <div className="text-center">
                            <div className="font-display text-2xl font-semibold text-[#111]" data-testid="brief-health-score">
                                {score == null ? "—" : score}
                            </div>
                            <div className="text-[10px] text-[#5E636E]">/100 {rating}</div>
                        </div>
                    </div>
                </div>
            </div>
            <ul className="mt-4 space-y-1.5 text-xs">
                {HEALTH_ROWS.map((r) => (
                    <li key={r.key} className="flex items-center justify-between" data-testid={`brief-health-${r.key}`}>
                        <span className="text-[#5E636E]">{r.label}</span>
                        <span className="font-medium text-[#111]">{b[r.key] ?? 0}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// -------------------------------------------------- Daybook Coach

function DaybookCoachCard({ brief }) {
    return (
        <div className="rounded-2xl bg-[#0d0d10] text-white p-5 flex flex-col" data-testid="brief-coach">
            <div className="flex items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-indigo-500 text-white">
                    <Plus className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="text-sm font-semibold">Daybook Coach</span>
            </div>
            <div className="mt-4 rounded-xl bg-white/10 p-4 text-sm leading-relaxed" data-testid="brief-coach-body">
                {brief.coach_summary || "I'll be back in the morning with a fresh review of your books."}
            </div>
            <div className="mt-auto pt-4">
                <div className="rounded-full bg-white/10 px-4 py-2.5 text-xs text-white/60">
                    Ask the Coach anything — coming soon
                </div>
            </div>
        </div>
    );
}

// -------------------------------------------------- TrustItem

function TrustItem({ Icon, title, body }) {
    return (
        <div className="flex items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#E4E4E7] text-[#0E3B2E]">
                <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
                <div className="text-sm font-semibold text-[#111]">{title}</div>
                <div className="text-xs text-[#5E636E]">{body}</div>
            </div>
        </div>
    );
}


// -------------------------------------------------- SourcesModal
// Explains where every figure in today's brief comes from. Live values pulled
// from GET /api/morning-brief/sources.

function SourcesModal({ clientId, onClose }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const params = clientId ? { client_id: clientId } : {};
        api.get("/morning-brief/sources", { params })
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Couldn't load sources"));
    }, [clientId]);

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="brief-sources-modal"
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-5 py-4 border-b border-[#E4E4E7]">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Where the numbers come from</div>
                        <div className="mt-0.5 font-display text-lg font-medium text-[#111]">Data preview</div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1 text-[#5E636E] hover:bg-zinc-100" data-testid="brief-sources-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {!data && !error && (
                        <div className="text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
                    )}
                    {error && <div className="text-sm text-rose-700">{error}</div>}
                    {data && !data.has_data && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                            No financials have been pushed for this client yet. Once your accountant connects Prosaic/Xero or pushes numbers manually, the brief will populate.
                        </div>
                    )}

                    {data && data.has_data && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                <SourceBadge source={data.source} />
                                {data.as_of_date && (
                                    <span className="text-[11px] text-[#5E636E]">
                                        As of <strong className="text-[#111]">{data.as_of_date}</strong>
                                    </span>
                                )}
                                {data.updated_at && (
                                    <span className="text-[11px] text-[#5E636E]">
                                        · Refreshed {new Date(data.updated_at).toLocaleString("en-NZ")}
                                    </span>
                                )}
                            </div>

                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Raw signals</div>
                            <ul className="mt-2 divide-y divide-[#E4E4E7]" data-testid="brief-sources-signals">
                                {data.signals.map((s) => (
                                    <li key={s.key} className="py-2.5">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <div className="text-sm font-medium text-[#111]">{s.label}</div>
                                            <div className="text-sm font-semibold tabular-nums text-[#111]">{fmtValue(s.value, s.format)}</div>
                                        </div>
                                        <div className="mt-0.5 text-[11px] text-[#5E636E]">{s.hint}</div>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-5 text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Derived (used in the brief text)</div>
                            <ul className="mt-2 divide-y divide-[#E4E4E7]" data-testid="brief-sources-derived">
                                {data.derived.map((m) => (
                                    <li key={m.key} className="py-2.5">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <div className="text-sm font-medium text-[#111]">{m.label}</div>
                                            <div className="text-sm font-semibold tabular-nums text-[#111]">{fmtValue(m.value, m.format)}</div>
                                        </div>
                                        <div className="mt-0.5 text-[11px] font-mono text-[#5E636E]">{m.formula}</div>
                                    </li>
                                ))}
                            </ul>

                            {data.tax_context && (
                                <div className="mt-5 rounded-md bg-[#F5F4EF] border border-[#E4E4E7] p-3">
                                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Approved tax context</div>
                                    <div className="mt-2 grid grid-cols-2 gap-y-1 text-xs">
                                        <span className="text-[#5E636E]">Reserve recommended</span>
                                        <span className="text-right tabular-nums font-medium">{fmtValue(data.tax_context.reserve_recommended, "money")}</span>
                                        <span className="text-[#5E636E]">Funding ratio</span>
                                        <span className="text-right tabular-nums font-medium">{fmtValue(data.tax_context.funding_ratio, "ratio")}</span>
                                        <span className="text-[#5E636E]">Period end</span>
                                        <span className="text-right font-medium">{data.tax_context.period_end || "—"}</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-[#E4E4E7] bg-[#FAFAFA] rounded-b-2xl">
                    <p className="text-[11px] text-[#5E636E]">
                        Questions about a figure? Ping your accountant via the Messages tab — they can walk you through it.
                    </p>
                </div>
            </div>
        </div>
    );
}

function SourceBadge({ source }) {
    const map = {
        xero: { label: "Xero", cls: "bg-sky-100 text-sky-800 border-sky-200" },
        prosaic: { label: "Prosaic", cls: "bg-violet-100 text-violet-800 border-violet-200" },
        webhook: { label: "External push", cls: "bg-zinc-100 text-zinc-800 border-zinc-200" },
        manual: { label: "Manual entry", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    };
    const cfg = map[source] || { label: source || "Unknown", cls: "bg-zinc-100 text-zinc-800 border-zinc-200" };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.cls}`}>
            Source · {cfg.label}
        </span>
    );
}

